/* ─────────────────────────────────────────────────────────────
   MapaSubpuntos.jsx — DMU 614 / Sprint Camellones (d)

   Renderiza una de 4 variantes según `tipo_layout`:
     · TREBOL_4_RAMAS   → SVG 320×320 con 4 cuadrantes + cruz central
     · CAMELLON_LINEAL  → segmentos horizontales con scroll
     · CUADRICULA       → grid 2×2 o 3×3 simple
     · POLIGONOS_MAPA   → Leaflet (OpenStreetMap) + polígonos
     · GENERICO         → lista botones

   Estados visuales por subpunto:
     🟢 REGADA     ─ verde con ✓ (no tocable)
     🟠 SELECCIONADA ─ naranja parpadea
     🟡 SUGERIDA   ─ amarillo parpadea (no seleccionada)
     ⚪ PENDIENTE  ─ gris claro tocable
     🔵 GPS chofer ─ punto azul parpadeante
   ───────────────────────────────────────────────────────────── */

import { useMemo } from "react";
import "../styles/mapa-subpuntos.css";

function estadoVisual({ sp, sugerido_id, seleccionado_id, estados_hoy }) {
  if (estados_hoy?.[sp.id] === "REGADA") return "regada";
  if (seleccionado_id === sp.id) return "seleccionada";
  if (sugerido_id === sp.id) return "sugerida";
  return "pendiente";
}

/* ═══════════════════════════════════════════════════════════
   LAYOUT 1 — TREBOL_4_RAMAS
   ═══════════════════════════════════════════════════════════ */
function LayoutTrebol({ subpuntos, sugerido_id, seleccionado_id, estados_hoy, onTap }) {
  const SIZE = 320;
  return (
    <div className="msp-stage" data-testid="mapa-trebol" style={{ width: SIZE, height: SIZE }}>
      <svg viewBox="0 0 320 320" width={SIZE} height={SIZE}>
        {/* Cruz central — dos avenidas */}
        <line x1="0"   y1="160" x2="320" y2="160" stroke="#cbd5e1" strokeWidth="6" />
        <line x1="160" y1="0"   x2="160" y2="320" stroke="#cbd5e1" strokeWidth="6" />
        <text x="158" y="14" textAnchor="end" fontSize="10" fill="#64748b">CANAL ▲</text>
        <text x="318" y="155" textAnchor="end" fontSize="10" fill="#64748b">◀ UNIVERSIDAD</text>
      </svg>

      {/* 4 cuadrantes tocables superpuestos */}
      {subpuntos.map((sp) => {
        const x = sp.posicion_visual?.x_pct ?? 50;
        const y = sp.posicion_visual?.y_pct ?? 50;
        const estado = estadoVisual({ sp, sugerido_id, seleccionado_id, estados_hoy });
        const cuad = sp.posicion_visual?.cuadrante || "";
        return (
          <button
            key={sp.id}
            data-testid={`subpunto-${sp.codigo}`}
            className={`msp-rama msp-${estado}`}
            style={{
              left: `${x - 22}%`,
              top: `${y - 22}%`,
              width: "44%",
              height: "44%",
            }}
            disabled={estado === "regada"}
            onClick={() => estado !== "regada" && onTap?.(sp.id)}
            aria-label={`${sp.codigo} — ${estado}`}
          >
            <div className="msp-rama-inner">
              <span className="msp-cuad-lbl">{cuad}</span>
              <span className="msp-rama-name">{sp.codigo.replace("RAMA_", "Rama ")}</span>
              <span className="msp-rama-dist">
                {estado === "regada" && (<><i className="fa-solid fa-check"/> Regada</>)}
                {estado === "sugerida" && (<>📍 {sp.distancia_gps_m}m · sugerida</>)}
                {estado === "seleccionada" && (<>✓ Seleccionada</>)}
                {estado === "pendiente" && <>Tocar para seleccionar</>}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LAYOUT 2 — CAMELLON_LINEAL
   ═══════════════════════════════════════════════════════════ */
function LayoutCamellon({ subpuntos, sugerido_id, seleccionado_id, estados_hoy, onTap }) {
  // Si no hay subpuntos (camellón lineal sin tramos georreferenciados)
  if (!subpuntos?.length) {
    return (
      <div className="msp-camellon-empty" data-testid="mapa-camellon-empty">
        <i className="fa-solid fa-road-circle-check" />
        <p>Este camellón se atiende como un solo tramo continuo.</p>
        <p className="msp-hint">Sube tus fotos y registra los litros cargados; el sistema asignará la vuelta al camellón completo.</p>
      </div>
    );
  }
  return (
    <div className="msp-camellon-wrap" data-testid="mapa-camellon">
      <div className="msp-camellon-track">
        {subpuntos.map((sp, idx) => {
          const estado = estadoVisual({ sp, sugerido_id, seleccionado_id, estados_hoy });
          return (
            <button
              key={sp.id}
              data-testid={`tramo-${idx + 1}`}
              className={`msp-tramo msp-${estado}`}
              disabled={estado === "regada"}
              onClick={() => estado !== "regada" && onTap?.(sp.id)}
            >
              <div className="msp-tramo-num">{idx + 1}</div>
              <div className="msp-tramo-name">{sp.codigo || sp.nombre}</div>
              {estado === "sugerida" && <i className="fa-solid fa-location-arrow msp-arrow"/>}
              {estado === "regada" && <i className="fa-solid fa-check msp-check"/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LAYOUT 3 — CUADRICULA
   ═══════════════════════════════════════════════════════════ */
function LayoutCuadricula({ subpuntos, sugerido_id, seleccionado_id, estados_hoy, onTap }) {
  const cols = subpuntos.length <= 4 ? 2 : 3;
  return (
    <div className="msp-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }} data-testid="mapa-cuadricula">
      {subpuntos.map((sp) => {
        const estado = estadoVisual({ sp, sugerido_id, seleccionado_id, estados_hoy });
        return (
          <button
            key={sp.id}
            data-testid={`celda-${sp.codigo}`}
            className={`msp-celda msp-${estado}`}
            disabled={estado === "regada"}
            onClick={() => estado !== "regada" && onTap?.(sp.id)}
          >
            <div className="msp-celda-name">{sp.codigo || sp.nombre}</div>
            <div className="msp-celda-dist">
              {estado === "regada" && <><i className="fa-solid fa-check"/> Regada</>}
              {estado === "sugerida" && <>📍 sugerida</>}
              {estado === "seleccionada" && <>✓ Seleccionada</>}
              {estado === "pendiente" && <>Tocar</>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   LAYOUT 4 — POLIGONOS_MAPA (Leaflet)
   ═══════════════════════════════════════════════════════════ */
function LayoutPoligonos({ subpuntos, sugerido_id, seleccionado_id, estados_hoy, gps_actual, onTap }) {
  // Importes diferidos para no romper SSR/lint si Leaflet falla
  const { MapContainer, TileLayer, CircleMarker, Marker, Popup } = require("react-leaflet");
  require("leaflet/dist/leaflet.css");

  const center = useMemo(() => {
    if (gps_actual) return [gps_actual.lat, gps_actual.lon];
    if (subpuntos[0]) return [subpuntos[0].gps_lat, subpuntos[0].gps_lon];
    return [28.6424, -106.0761];
  }, [gps_actual, subpuntos]);

  return (
    <div className="msp-mapa-wrap" data-testid="mapa-poligonos">
      <MapContainer center={center} zoom={18} style={{ height: 360, width: "100%", borderRadius: 12 }}>
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OSM" />
        {subpuntos.map((sp) => {
          const estado = estadoVisual({ sp, sugerido_id, seleccionado_id, estados_hoy });
          const color =
            estado === "regada" ? "#10b981" :
            estado === "seleccionada" ? "#fb923c" :
            estado === "sugerida" ? "#fde047" :
            "#94a3b8";
          return (
            <CircleMarker
              key={sp.id}
              center={[sp.gps_lat, sp.gps_lon]}
              radius={18}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.6, weight: 3 }}
              eventHandlers={{ click: () => estado !== "regada" && onTap?.(sp.id) }}
            >
              <Popup>{sp.codigo || sp.nombre} · {estado}</Popup>
            </CircleMarker>
          );
        })}
        {gps_actual && (
          <CircleMarker
            center={[gps_actual.lat, gps_actual.lon]}
            radius={8}
            pathOptions={{ color: "#2563eb", fillColor: "#3b82f6", fillOpacity: 1 }}
          />
        )}
      </MapContainer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
   ═══════════════════════════════════════════════════════════ */
export default function MapaSubpuntos({
  tipo_layout,
  subpuntos = [],
  subpunto_sugerido_id = null,
  subpunto_seleccionado_id = null,
  estados_hoy = {},
  gps_actual = null,
  onSubpuntoTap,
  cobertura_pct = 0,
}) {
  const total = subpuntos.length;
  const regadas = subpuntos.filter((s) => estados_hoy[s.id] === "REGADA").length;

  const handleTap = (sp_id) => {
    // método se calcula al confirmar; aquí solo cambia selección
    onSubpuntoTap?.(sp_id, "TOCK_MANUAL");
  };

  let Layout = null;
  if (tipo_layout === "TREBOL_4_RAMAS")        Layout = LayoutTrebol;
  else if (tipo_layout === "CAMELLON_LINEAL")  Layout = LayoutCamellon;
  else if (tipo_layout === "CUADRICULA")       Layout = LayoutCuadricula;
  else if (tipo_layout === "POLIGONOS_MAPA")   Layout = LayoutPoligonos;
  else                                          Layout = LayoutCuadricula;

  return (
    <div className="msp-root" data-testid="mapa-subpuntos">
      {/* Barra de cobertura */}
      <div className="msp-cob-bar" data-testid="cobertura-bar">
        <div className="msp-cob-track">
          <div className="msp-cob-fill" style={{ width: `${cobertura_pct}%` }} />
        </div>
        <div className="msp-cob-text">
          {cobertura_pct}% <span>({regadas} de {total} {tipo_layout === "TREBOL_4_RAMAS" ? "ramas" : "tramos"})</span>
        </div>
      </div>

      {/* Layout específico */}
      <Layout
        subpuntos={subpuntos}
        sugerido_id={subpunto_sugerido_id}
        seleccionado_id={subpunto_seleccionado_id}
        estados_hoy={estados_hoy}
        gps_actual={gps_actual}
        onTap={handleTap}
      />

      {/* Leyenda */}
      <div className="msp-legend" data-testid="mapa-legend">
        <span><i className="dot dot-pendiente"/> Pendiente</span>
        <span><i className="dot dot-sugerida"/> Sugerida por GPS</span>
        <span><i className="dot dot-seleccionada"/> Seleccionada</span>
        <span><i className="dot dot-regada"/> Regada hoy</span>
        {gps_actual && <span><i className="dot dot-gps"/> Tu GPS</span>}
      </div>
    </div>
  );
}
